import React, { useState, useEffect, useCallback } from 'react';
import { MdLock, MdPersonAdd, MdEdit, MdList, MdClear, MdSettings, MdExpandMore, MdExpandLess } from 'react-icons/md';
import axiosInstance from '../../../../axiosConfig';

const DialogBox = ({ type, message, onConfirm, onCancel, isOpen, setIsOpen }) => {
  if (!isOpen) return null;

  const styles = {
    info: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-500' },
    error: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' },
    success: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500' },
    prompt: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-500' },
  };

  const { bg, text, border } = styles[type] || styles.info;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`p-6 ${bg} ${text} rounded-lg shadow-xl border ${border} max-w-md w-full`}>
        <p className="text-lg font-medium mb-4">{message}</p>
        <div className="flex justify-end space-x-4">
          {type === 'prompt' && (
            <>
              <button
                onClick={() => {
                  onCancel && onCancel();
                  setIsOpen(false);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onConfirm && onConfirm();
                  setIsOpen(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Confirm
              </button>
            </>
          )}
          {type !== 'prompt' && (
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const UserMaintenance = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    userTimezone: 'Africa/Nairobi',
    role: '',
    profile: '',
    isApproved: false,
    entryStatus: false,
    entryType: 'user',
    department: '',
    accountExpiry: '',
    mfaEnabled: false,
  });
  const [setEditUserId] = useState(null);
  const [availableRights, setAvailableRights] = useState({});
  const [assignedRights, setAssignedRights] = useState([]);
  const [expandedFunctionalities, setExpandedFunctionalities] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, type: '', message: '', onConfirm: null, onCancel: null });
  const [isActionsExpanded, setIsActionsExpanded] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/usr-lst');
        if (response.data.status === 'success') {
          setUsers(response.data.message);
          console.log('Users fetched:', response.data.message);
        } else {
          setDialog({
            isOpen: true,
            type: response.data.status,
            message: response.data.message || 'Failed to fetch users.',
          });
        }
      } catch (err) {
        setDialog({
          isOpen: true,
          type: 'error',
          message: err.response?.data?.message || 'Failed to fetch users. Please try again.',
        });
        console.error('Fetch users error:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchGroups = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/group-lst');
        if (response.data.status === 'success') {
          setGroups(response.data.message);
          console.log('Groups fetched:', response.data.message);
        } else {
          setDialog({
            isOpen: true,
            type: response.data.status,
            message: response.data.message || 'Failed to fetch groups.',
          });
        }
      } catch (err) {
        setDialog({
          isOpen: true,
          type: 'error',
          message: err.response?.data?.message || 'Failed to fetch groups. Please try again.',
        });
        console.error('Fetch groups error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
    fetchGroups();
  }, []);

  const fetchUserPermissions = useCallback(async (userId) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/get-usr-perms?user_id=${userId}`);
      if (response.data.status === 'success') {
        const { available_rights, user_rights } = response.data.message;
        setAvailableRights(available_rights || {});
        setAssignedRights(user_rights || []);
        setExpandedFunctionalities(
          Object.keys(available_rights || {}).reduce((acc, key) => ({ ...acc, [key]: false }), {})
        );
        console.log('User permissions fetched:', { available_rights, user_rights });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || 'Failed to fetch user permissions.',
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Failed to fetch user permissions. Please try again.',
      });
      console.error('Fetch permissions error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    console.log('Input changed:', { name, value: type === 'checkbox' ? checked : value });
  }, []);

  const handleUserSelect = useCallback((userId) => {
    const user = users.find((u) => u.id === userId);
    console.log('handleUserSelect - userId:', userId, 'user:', user);

    const isSelected = selectedUser === userId;
    setSelectedUser(isSelected ? null : userId);

    if (user && !isSelected) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        userTimezone: user.timezone || 'Africa/Nairobi',
        role: user.role || '',
        profile: user.profile || '',
        isApproved: user.approved || false,
        entryStatus: user.entry_status || false,
        entryType: user.entry_type || 'user',
        department: user.department || '',
        accountExpiry: user.account_expiry || '',
        mfaEnabled: user.mfa_enabled || false,
      });
      setAssignedRights(user.rights || []);
      setEditUserId(userId);
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        userTimezone: 'Africa/Nairobi',
        role: '',
        profile: '',
        isApproved: false,
        entryStatus: false,
        entryType: 'user',
        department: '',
        accountExpiry: '',
        mfaEnabled: false,
      });
      setAssignedRights([]);
      setEditUserId(null);
    }

    console.log('handleUserSelect - formData reset or filled');
  }, [users, selectedUser, setEditUserId]);

  const handleCreateUser = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        user_timezone: formData.userTimezone,
        profile: formData.profile,
        role: formData.role,
        is_approved: formData.isApproved,
        entry_status: formData.entryStatus,
        entry_type: formData.entryType,
        department: formData.department,
        rights: assignedRights,
        account_expiry: formData.accountExpiry,
        mfa_enabled: formData.mfaEnabled,
      };
      const response = await axiosInstance.post('/create-usr', payload);
      setDialog({
        isOpen: true,
        type: response.data.status,
        message: response.data.message || (response.data.status === 'success' ? 'User created successfully!' : 'Failed to create user'),
      });
      if (response.data.status === 'success') {
        const usersResponse = await axiosInstance.get('/usr-lst');
        if (usersResponse.data.status === 'success') {
          setUsers(usersResponse.data.message);
        }
        setFormData({
          name: '',
          email: '',
          phone: '',
          userTimezone: 'Africa/Nairobi',
          role: '',
          profile: '',
          isApproved: false,
          entryStatus: false,
          entryType: 'user',
          department: '',
          accountExpiry: '',
          mfaEnabled: false,
        });
        setAssignedRights([]);
        setAvailableRights({});
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error creating user',
      });
      console.error('Create user error:', err);
    } finally {
      setLoading(false);
    }
  }, [formData, assignedRights]);

  const handleClearForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      userTimezone: 'Africa/Nairobi',
      role: '',
      profile: '',
      isApproved: false,
      entryStatus: false,
      entryType: 'user',
      department: '',
      accountExpiry: '',
      mfaEnabled: false,
    });
    setAssignedRights([]);
    setAvailableRights({});
    console.log('Form cleared');
  }, []);

  const handleEnableDisableUser = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    const enable = !formData.entryStatus;
    try {
      const response = await axiosInstance.post(`/user-enable-disable?user_id=${selectedUser}&enable=${enable}`);
      if (response.data.status === 'success') {
        setUsers(users.map((user) => (user.id === selectedUser ? { ...user, entry_status: enable } : user)));
        setFormData({ ...formData, entryStatus: enable });
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || `User ${enable ? 'enabled' : 'disabled'} successfully!`,
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || `Failed to ${enable ? 'enable' : 'disable'} user.`,
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || `Error ${enable ? 'enabling' : 'disabling'} user.`,
      });
      console.error('Enable/disable user error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, formData, users]);

  const handleUnlockAccount = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/unlock-user?user_id=${selectedUser}`);
      if (response.data.status === 'success') {
        setUsers(users.map((user) => (user.id === selectedUser ? { ...user, is_locked: false } : user)));
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || 'Account unlocked successfully!',
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || 'Failed to unlock account.',
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error unlocking account.',
      });
      console.error('Unlock account error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, users]);

  const handleBlockUnblockUser = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    const isBlocked = users.find((u) => u.id === selectedUser)?.is_blocked;
    try {
      const response = await axiosInstance.post(`/block-user?user_id=${selectedUser}&block=${!isBlocked}`);
      if (response.data.status === 'success') {
        setUsers(users.map((user) => (user.id === selectedUser ? { ...user, is_blocked: !isBlocked } : user)));
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || `User ${!isBlocked ? 'blocked' : 'unblocked'} successfully!`,
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || `Failed to ${!isBlocked ? 'block' : 'unblock'} user.`,
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || `Error ${!isBlocked ? 'blocking' : 'unblocking'} user.`,
      });
      console.error('Block/unblock user error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, users]);

  const handleDeleteUser = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const response = await axiosInstance.delete(`/delete_user?user_id=${selectedUser}`);
      if (response.data.status === 'success') {
        setUsers(users.filter((user) => user.id !== selectedUser));
        setSelectedUser(null);
        setFormData({
          name: '',
          email: '',
          phone: '',
          userTimezone: 'Africa/Nairobi',
          role: '',
          profile: '',
          isApproved: false,
          entryStatus: false,
          entryType: 'user',
          department: '',
          accountExpiry: '',
          mfaEnabled: false,
        });
        setAssignedRights([]);
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || 'User deleted successfully!',
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || 'Failed to delete user.',
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error deleting user.',
      });
      console.error('Delete user error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, users]);

  const handleSuspendUser = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/suspend-user?user_id=${selectedUser}`);
      if (response.data.status === 'success') {
        setUsers(users.map((user) => (user.id === selectedUser ? { ...user, is_suspended: true } : user)));
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || 'User suspended successfully!',
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || 'Failed to suspend user.',
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error suspending user.',
      });
      console.error('Suspend user error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, users]);

  const handleToggleMFA = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    const enableMFA = !formData.mfaEnabled;
    try {
      const response = await axiosInstance.post(`/toggle-mfa?user_id=${selectedUser}&enable=${enableMFA}`);
      if (response.data.status === 'success') {
        setUsers(users.map((user) => (user.id === selectedUser ? { ...user, mfa_enabled: enableMFA } : user)));
        setFormData({ ...formData, mfaEnabled: enableMFA });
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || `MFA ${enableMFA ? 'enabled' : 'disabled'} successfully!`,
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || `Failed to ${enableMFA ? 'enable' : 'disable'} MFA.`,
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || `Error ${enableMFA ? 'enabling' : 'disabling'} MFA.`,
      });
      console.error('Toggle MFA error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, formData, users]);

  // resetting users pass
  const handleResetUser = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/reset-usr?user_id=${selectedUser}`);
      if (response.data.status === 'success') {
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || 'User reset successfully! Password has been reset and email sent.',
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || 'Failed to reset user.',
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error resetting user.',
      });
      console.error('Reset user error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser]);

  // password change
  const handleEnforcePasswordChange = useCallback(async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/force-pwd-change?user_id=${selectedUser}`);
      if (response.data.status === 'success') {
        setDialog({
          isOpen: true,
          type: 'success',
          message: response.data.message || 'Password change enforced successfully! User will be required to change password on next login.',
        });
      } else {
        setDialog({
          isOpen: true,
          type: response.data.status,
          message: response.data.message || 'Failed to enforce password change.',
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error enforcing password change.',
      });
      console.error('Enforce password change error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser]);

  const handleToggleFunctionality = useCallback((functionality) => {
    setExpandedFunctionalities((prev) => ({
      ...prev,
      [functionality]: !prev[functionality],
    }));
    console.log('Toggled functionality:', functionality);
  }, []);

  const handleAssignRight = useCallback((functionality, permission = null) => {
    if (permission) {
      if (!assignedRights.includes(permission)) {
        setAssignedRights((prev) => [...prev, permission]);
        console.log('Assigned permission:', permission);
      }
    } else {
      const permissions = availableRights[functionality] || [];
      setAssignedRights((prev) => [
        ...prev,
        ...permissions.filter((p) => !prev.includes(p)),
      ]);
      console.log('Assigned all permissions for functionality:', functionality);
    }
  }, [availableRights, assignedRights]);

  const handleUnassignRight = useCallback((permission) => {
    setAssignedRights((prev) => prev.filter((item) => item !== permission));
    console.log('Unassigned permission:', permission);
  }, []);

  const handleUpdateRights = useCallback(async () => {
    if (!selectedUser) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: 'No user selected to assign rights.',
      });
      return;
    }
    setLoading(true);
    try {
      const payload = { rights: assignedRights, user_id: selectedUser };
      const response = await axiosInstance.post('/update-user-rights', payload);
      setUsers(
        users.map((user) =>
          user.id === selectedUser ? { ...user, rights: assignedRights } : user
        )
      );
      setDialog({
        isOpen: true,
        type: response.data.status,
        message: response.data.message || 'Rights updated successfully!',
      });
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        message: err.response?.data?.message || 'Error updating user rights',
      });
      console.error('Update rights error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, assignedRights, users]);

  const selectUserToEdit = useCallback((user) => {
    console.log('selectUserToEdit - user:', user);
    setSelectedUser(user.id);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      userTimezone: user.timezone || 'Africa/Nairobi',
      role: user.role || '',
      profile: user.profile || '',
      isApproved: user.approved || false,
      entryStatus: user.entry_status || false,
      entryType: user.entry_type || 'user',
      department: user.department || '',
      accountExpiry: user.account_expiry || '',
      mfaEnabled: user.mfa_enabled || false,
    });
    setIsActionsExpanded(false);
    setActiveTab('actions');
    setDialog({ isOpen: false, type: '', message: '', onConfirm: null, onCancel: null });
    console.log('selectUserToEdit - selectedUser:', user.id, 'isActionsExpanded:', isActionsExpanded);
  }, [isActionsExpanded]);

  const roles = groups.filter((group) => group.group_type === 'role');
  const profiles = groups.filter((group) => group.group_type === 'profile');
  const departments = groups.filter((group) => group.group_type === 'department');

  const tabs = [
    { tab: 'list', label: 'User Listing', icon: MdList },
    { tab: 'create', label: 'Create User', icon: MdPersonAdd },
    { tab: 'rights', label: 'User Rights', icon: MdLock },
    { tab: 'edit', label: 'Edit User', icon: MdEdit },
    { tab: 'actions', label: 'Actions', icon: MdSettings },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">User Listing</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profile
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedUser === user.id}
                          onChange={() => {
                            handleUserSelect(user.id);
                            if (activeTab === 'rights') {
                              fetchUserPermissions(user.id);
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.role}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.profile}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.department}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {user.entry_status ? 'Active' : 'Inactive'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => selectUserToEdit(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Manage Actions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'create':
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">Create User</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!formData.name) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Name is required.',
                  });
                  return;
                }
                if (!formData.email) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Email is required.',
                  });
                  return;
                }
                if (!formData.role) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Role is required.',
                  });
                  return;
                }
                if (!formData.profile) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Profile is required.',
                  });
                  return;
                }
                if (!formData.department) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Department is required.',
                  });
                  return;
                }
                setDialog({
                  isOpen: true,
                  type: 'prompt',
                  message: 'Are you sure you want to create this user?',
                  onConfirm: handleCreateUser,
                });
              }}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.name === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                />
                {formData.name === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Name is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="new-email"
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.email === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                />
                {formData.email === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Email is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Timezone</label>
                <select
                  name="userTimezone"
                  value={formData.userTimezone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="Africa/Nairobi">Africa/Nairobi</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New York</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Africa/Abidjan">Africa/Abidjan</option>
                  <option value="EST">EST</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.role === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={`${role.group_type}-${role.name}`} value={role.name}>
                      {role.name} {role.description ? `(${role.description})` : ''}
                    </option>
                  ))}
                </select>
                {formData.role === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Role is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Profile</label>
                <select
                  name="profile"
                  value={formData.profile}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.profile === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                >
                  <option value="">Select Profile</option>
                  {profiles.map((profile) => (
                    <option key={`${profile.group_type}-${profile.name}`} value={profile.name}>
                      {profile.name} {profile.description ? `(${profile.description})` : ''}
                    </option>
                  ))}
                </select>
                {formData.profile === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Profile is required</p>
                )}
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isApproved"
                    checked={formData.isApproved}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Approved</span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="entryStatus"
                    checked={formData.entryStatus}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Entry Status</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Entry Type</label>
                <input
                  type="text"
                  name="entryType"
                  value={formData.entryType}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  maxLength="30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Department</label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.department === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={`${dept.group_type}-${dept.name}`} value={dept.name}>
                      {dept.name} {dept.description ? `(${dept.description})` : ''}
                    </option>
                  ))}
                </select>
                {formData.department === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Department is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Account Expiry</label>
                <input
                  type="date"
                  name="accountExpiry"
                  value={formData.accountExpiry}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="mfaEnabled"
                    checked={formData.mfaEnabled}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable MFA</span>
                </label>
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={loading}
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={handleClearForm}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  <MdClear className="inline h-5 w-5 mr-2" />
                  Clear Form
                </button>
              </div>
            </form>
          </div>
        );
      case 'rights':
        if (!selectedUser) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800">User Rights</h2>
              <DialogBox
                type="error"
                message="No user selected to assign rights."
                isOpen={dialog.isOpen}
                setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
              />
            </div>
          );
        }
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">User Rights</h2>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-700">Selected User</h3>
              <p className="text-sm text-gray-700">
                {users.find((u) => u.id === selectedUser)?.email || 'Unknown'}
              </p>
            </div>
            <div className="flex space-x-4">
              <div className="w-1/2 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Available Rights</h3>
                <div className="space-y-2">
                  {Object.keys(availableRights).map((functionality) => (
                    <div key={functionality} className="border-b border-gray-300">
                      <div
                        className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                        onClick={() => handleToggleFunctionality(functionality)}
                      >
                        <span>{functionality}</span>
                        <button
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignRight(functionality);
                          }}
                        >
                          Select All
                        </button>
                        {expandedFunctionalities[functionality] ? (
                          <MdExpandLess className="h-5 w-5" />
                        ) : (
                          <MdExpandMore className="h-5 w-5" />
                        )}
                      </div>
                      {expandedFunctionalities[functionality] && (
                        <div className="ml-4 space-y-1">
                          {availableRights[functionality].map((permission) => (
                            <div
                              key={permission}
                              className="p-2 bg-white rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                              onClick={() => handleAssignRight(functionality, permission)}
                            >
                              {permission}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-1/2 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Assigned Rights</h3>
                <div className="space-y-2">
                  {assignedRights.map((right) => (
                    <div
                      key={right}
                      className="p-2 bg-white rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                      onClick={() => handleUnassignRight(right)}
                    >
                      {right}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button
              className="w-full mt-4 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
              onClick={handleUpdateRights}
              disabled={loading}
            >
              Update Rights
            </button>
          </div>
        );
      case 'edit':
        if (!selectedUser) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800">Edit User</h2>
              <DialogBox
                type="error"
                message="No user selected to edit."
                isOpen={dialog.isOpen}
                setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
              />
            </div>
          );
        }
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">Edit User</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!formData.name) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Name is required.',
                  });
                  return;
                }
                if (!formData.email) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Email is required.',
                  });
                  return;
                }
                if (!formData.role) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Role is required.',
                  });
                  return;
                }
                if (!formData.profile) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Profile is required.',
                  });
                  return;
                }
                if (!formData.department) {
                  setDialog({
                    isOpen: true,
                    type: 'error',
                    message: 'Department is required.',
                  });
                  return;
                }
                setDialog({
                  isOpen: true,
                  type: 'prompt',
                  message: 'Are you sure you want to update this user?',
                  onConfirm: async () => {
                    setLoading(true);
                    try {
                      const payload = {
                        user_id: selectedUser,
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        user_timezone: formData.userTimezone,
                        profile: formData.profile,
                        role: formData.role,
                        is_approved: formData.isApproved,
                        entry_status: formData.entryStatus,
                        entry_type: formData.entryType,
                        department: formData.department,
                        account_expiry: formData.accountExpiry,
                        mfa_enabled: formData.mfaEnabled,
                      };
                      const response = await axiosInstance.post('/update-user', payload);
                      setDialog({
                        isOpen: true,
                        type: response.data.status,
                        message: response.data.message || (response.data.status === 'success' ? 'User updated successfully!' : 'Failed to update user'),
                      });
                      if (response.data.status === 'success') {
                        const usersResponse = await axiosInstance.get('/usr-lst');
                        if (usersResponse.data.status === 'success') {
                          setUsers(usersResponse.data.message);
                        }
                      }
                    } catch (err) {
                      setDialog({
                        isOpen: true,
                        type: 'error',
                        message: err.response?.data?.message || 'Error updating user',
                      });
                      console.error('Update user error:', err);
                    } finally {
                      setLoading(false);
                    }
                  },
                });
              }}
              className="space-y-6"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.name === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                />
                {formData.name === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Name is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.email === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                />
                {formData.email === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Email is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Timezone</label>
                <select
                  name="userTimezone"
                  value={formData.userTimezone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="Africa/Nairobi">Africa/Nairobi</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New York</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Africa/Abidjan">Africa/Abidjan</option>
                  <option value="EST">EST</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.role === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={`${role.group_type}-${role.name}`} value={role.name}>
                      {role.name} {role.description ? `(${role.description})` : ''}
                    </option>
                  ))}
                </select>
                {formData.role === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Role is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Profile</label>
                <select
                  name="profile"
                  value={formData.profile}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.profile === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                >
                  <option value="">Select Profile</option>
                  {profiles.map((profile) => (
                    <option key={`${profile.group_type}-${profile.name}`} value={profile.name}>
                      {profile.name} {profile.description ? `(${profile.description})` : ''}
                    </option>
                  ))}
                </select>
                {formData.profile === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Profile is required</p>
                )}
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isApproved"
                    checked={formData.isApproved}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Approved</span>
                </label>
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="entryStatus"
                    checked={formData.entryStatus}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Entry Status</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Entry Type</label>
                <input
                  type="text"
                  name="entryType"
                  value={formData.entryType}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  maxLength="30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Department</label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${formData.department === '' && dialog.message ? 'border-red-500' : ''
                    }`}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={`${dept.group_type}-${dept.name}`} value={dept.name}>
                      {dept.name} {dept.description ? `(${dept.description})` : ''}
                    </option>
                  ))}
                </select>
                {formData.department === '' && dialog.message && (
                  <p className="mt-1 text-sm text-red-600">Department is required</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Account Expiry</label>
                <input
                  type="date"
                  name="accountExpiry"
                  value={formData.accountExpiry}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="mfaEnabled"
                    checked={formData.mfaEnabled}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable MFA</span>
                </label>
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                  disabled={loading}
                >
                  Update User
                </button>
                <button
                  type="button"
                  onClick={handleClearForm}
                  className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  <MdClear className="inline h-5 w-5 mr-2" />
                  Clear Form
                </button>
              </div>
            </form>
          </div>
        );
      case 'actions':
        if (!selectedUser) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-6 text-gray-800">User Actions Management</h2>
              <DialogBox
                type="error"
                message="No user selected to manage actions. Please select a user from the User Listing tab."
                isOpen={dialog.isOpen}
                setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
              />
            </div>
          );
        }
        return (
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">User Actions Management</h2>
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-700">Selected User</h3>
              <p className="text-sm text-gray-700">
                {users.find((u) => u.id === selectedUser)?.email || 'Unknown'}
              </p>
            </div>
            <div className="mb-6">
              <button
                onClick={() => {
                  setIsActionsExpanded(!isActionsExpanded);
                  console.log('Toggled isActionsExpanded:', !isActionsExpanded);
                }}
                className="flex items-center w-full p-3 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                <span className="text-lg font-medium text-gray-700">User Actions</span>
                {isActionsExpanded ? (
                  <MdExpandLess className="ml-auto h-5 w-5 text-gray-600" />
                ) : (
                  <MdExpandMore className="ml-auto h-5 w-5 text-gray-600" />
                )}
              </button>
              <div className={`${isActionsExpanded ? 'block' : 'hidden'} mt-4 space-y-4`}>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: `Are you sure you want to ${formData.entryStatus ? 'disable' : 'enable'} this user?`,
                        onConfirm: handleEnableDisableUser,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    {formData.entryStatus ? 'Disable User' : 'Enable User'}
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: 'Are you sure you want to unlock this user\'s account?',
                        onConfirm: handleUnlockAccount,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    Unlock Account
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: `Are you sure you want to ${users.find((u) => u.id === selectedUser)?.is_blocked ? 'unblock' : 'block'
                          } this user?`,
                        onConfirm: handleBlockUnblockUser,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    {users.find((u) => u.id === selectedUser)?.is_blocked ? 'Unblock User' : 'Block User'}
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: 'Are you sure you want to delete this user?',
                        onConfirm: handleDeleteUser,
                      })
                    }
                    className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                    disabled={loading}
                  >
                    Delete User
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: 'Are you sure you want to suspend this user?',
                        onConfirm: handleSuspendUser,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    Suspend User
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: `Are you sure you want to ${formData.mfaEnabled ? 'disable' : 'enable'} MFA for this user?`,
                        onConfirm: handleToggleMFA,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    {formData.mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: 'Are you sure you want to reset this user? This will reset their password and send them an email.',
                        onConfirm: handleResetUser,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() =>
                      setDialog({
                        isOpen: true,
                        type: 'prompt',
                        message: 'Are you sure you want to enforce password change for this user? They will be required to change their password on next login.',
                        onConfirm: handleEnforcePasswordChange,
                      })
                    }
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                    disabled={loading}
                  >
                    Enforce Password Change
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative container mx-auto p-4">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="animate-spin h-12 w-12 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
        </div>
      )}
      <div className={loading ? 'pointer-events-none opacity-50' : ''}>
        <div className="mb-6">
          <div className="flex space-x-4 border-b border-gray-200">
            {tabs.map(({ tab, label, icon: Icon }) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'rights' && selectedUser) {
                    fetchUserPermissions(selectedUser);
                  }
                  setActiveTab(tab);
                  if (tab === 'actions' && !selectedUser) {
                    setDialog({
                      isOpen: true,
                      type: 'error',
                      message: 'No user selected to manage actions. Please select a user from the User Listing tab.',
                    });
                  } else {
                    setDialog({ isOpen: false, type: '', message: '', onConfirm: null, onCancel: null });
                  }
                }}
                className={`flex items-center px-4 py-2 text-sm font-medium ${activeTab === tab
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-gray-600 hover:text-indigo-600'
                  }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {label}
              </button>
            ))}
          </div>
        </div>
        {renderTabContent()}
        <DialogBox
          type={dialog.type}
          message={dialog.message}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          isOpen={dialog.isOpen}
          setIsOpen={(isOpen) => setDialog({ ...dialog, isOpen })}
        />
      </div>
    </div>
  );
};

export default UserMaintenance;